import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { fmtINR } from "@/lib/format";
import { Plus, Pencil, Trash2, Play, Zap } from "lucide-react";

const emptyRule = {
  name: "", patterns: [""], side: "any",
  account_id: "", amount_min: "", amount_max: "",
  narration: "", priority: 100, enabled: true, bank_account_id: "",
};

export default function BankRules() {
  const [rules, setRules] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [banks, setBanks] = useState([]);
  const [openEdit, setOpenEdit] = useState(false);
  const [form, setForm] = useState(emptyRule);
  const [busy, setBusy] = useState(false);
  const [applyPreview, setApplyPreview] = useState(null);

  const load = async () => {
    const [r, ac] = await Promise.all([
      api.get("/bank/rules"),
      api.get("/accounts"),
    ]);
    setRules(r.data);
    setAccounts(ac.data);
    setBanks(ac.data.filter((a) => a.subtype === "bank" || a.subtype === "cash"));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyRule); setOpenEdit(true); };
  const openExisting = (rule) => {
    setForm({
      ...emptyRule, ...rule,
      patterns: rule.patterns?.length ? rule.patterns : [""],
      amount_min: rule.amount_min ?? "",
      amount_max: rule.amount_max ?? "",
      bank_account_id: rule.bank_account_id || "",
    });
    setOpenEdit(true);
  };

  const save = async () => {
    if (!form.name || !form.account_id || !form.patterns.filter(Boolean).length) {
      toast.error("Name, account, and at least one pattern are required");
      return;
    }
    const payload = {
      ...form,
      patterns: form.patterns.filter((p) => p && p.trim()),
      amount_min: form.amount_min === "" ? null : Number(form.amount_min),
      amount_max: form.amount_max === "" ? null : Number(form.amount_max),
      bank_account_id: form.bank_account_id || null,
    };
    setBusy(true);
    try {
      if (form.id) {
        await api.patch(`/bank/rules/${form.id}`, payload);
        toast.success("Rule updated");
      } else {
        await api.post("/bank/rules", payload);
        toast.success("Rule created");
      }
      setOpenEdit(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete rule "${r.name}"?`)) return;
    try { await api.delete(`/bank/rules/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const toggleEnabled = async (r) => {
    try {
      await api.patch(`/bank/rules/${r.id}`, { enabled: !r.enabled });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const applyDryRun = async () => {
    setBusy(true);
    try {
      const r = await api.post("/bank/rules/apply", { mode: "dry_run" });
      setApplyPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const applyCommit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/bank/rules/apply", { mode: "commit" });
      toast.success(`Auto-posted ${r.data.posted} voucher(s) via bank rules`);
      setApplyPreview(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const accountLabel = (aid) => {
    const a = accounts.find((x) => x.id === aid);
    return a ? `${a.code} · ${a.name}` : "—";
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="bank-rules-page">
      <PageHeader
        eyebrow="Cash Management · Automation"
        title="Recurring Bank Rules"
        description="Define once — auto-post forever. Any bank statement row whose description contains your pattern automatically becomes a posted voucher against the target account. Rules fire during import AND on demand via 'Run rules'."
        actions={
          <>
            <Button onClick={applyDryRun} disabled={busy || rules.filter((r) => r.enabled).length === 0}
                    variant="outline" className="rounded-sm gap-1.5"
                    data-testid="run-rules-btn">
              <Play className="w-3.5 h-3.5" />{busy ? "Scanning…" : "Run rules"}
            </Button>
            <Button onClick={openNew} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-rule-btn">
              <Plus className="w-3.5 h-3.5" /> New Rule
            </Button>
          </>
        }
      />

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead>
            <tr>
              <th style={{ width: 40 }}></th>
              <th>Priority</th>
              <th>Name</th>
              <th>Patterns</th>
              <th>Side</th>
              <th>Post to</th>
              <th style={{ width: 100 }}></th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && (
              <tr><td colSpan="7" className="text-center py-8 text-muted-foreground">
                No rules yet. Click <b>New Rule</b> to save your first recurring pattern.
              </td></tr>
            )}
            {rules.map((r) => (
              <tr key={r.id} data-testid={`rule-row-${r.id}`}>
                <td>
                  <Checkbox checked={r.enabled} onCheckedChange={() => toggleEnabled(r)}
                             data-testid={`rule-toggle-${r.id}`} />
                </td>
                <td className="font-mono text-xs">{r.priority}</td>
                <td className="font-medium">{r.name}</td>
                <td className="text-xs">
                  {r.patterns?.map((p, i) => (
                    <Badge key={i} variant="outline" className="mr-1 font-mono text-[10px]">{p}</Badge>
                  ))}
                </td>
                <td>
                  <span className={`text-[10px] font-mono uppercase ${
                    r.side === "debit" ? "text-destructive" : r.side === "credit" ? "text-emerald-600" : "text-muted-foreground"
                  }`}>{r.side}</span>
                </td>
                <td className="text-xs">{accountLabel(r.account_id)}</td>
                <td className="text-right">
                  <button className="text-muted-foreground hover:text-accent p-1"
                    onClick={() => openExisting(r)}
                    data-testid={`edit-rule-${r.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button className="text-muted-foreground hover:text-destructive p-1 ml-1"
                    onClick={() => remove(r)}
                    data-testid={`delete-rule-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ---- Rule create/edit dialog ---- */}
      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent className="max-w-lg" data-testid="rule-dialog">
          <DialogHeader><DialogTitle>{form.id ? "Edit Rule" : "New Rule"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Rule Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="e.g. HDFC Home Loan EMI"
                     className="rounded-sm mt-1" data-testid="rule-name" />
            </div>
            <div>
              <Label>Patterns (description contains — case-insensitive)</Label>
              {form.patterns.map((p, i) => (
                <div key={i} className="flex gap-1 mt-1">
                  <Input value={p} onChange={(e) => {
                            const nx = [...form.patterns]; nx[i] = e.target.value;
                            setForm({ ...form, patterns: nx });
                          }}
                          placeholder="HDFC EMI"
                          className="rounded-sm" data-testid={`pattern-${i}`} />
                  {form.patterns.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => {
                              const nx = form.patterns.filter((_, ix) => ix !== i);
                              setForm({ ...form, patterns: nx });
                            }}><Trash2 className="w-3 h-3" /></Button>
                  )}
                </div>
              ))}
              <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, patterns: [...form.patterns, ""] })}
                      className="text-xs mt-1" data-testid="add-pattern">
                <Plus className="w-3 h-3 mr-1" /> Add pattern (any match wins)
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Side</Label>
                <Select value={form.side} onValueChange={(v) => setForm({ ...form, side: v })}>
                  <SelectTrigger className="rounded-sm mt-1 h-9" data-testid="rule-side"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="debit">Debit (money out)</SelectItem>
                    <SelectItem value="credit">Credit (money in)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority (lower = first)</Label>
                <Input type="number" value={form.priority}
                        onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
                        className="rounded-sm mt-1" data-testid="rule-priority" />
              </div>
            </div>
            <div>
              <Label>Post to Account *</Label>
              <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
                <SelectTrigger className="rounded-sm mt-1 h-9" data-testid="rule-account"><SelectValue placeholder="Pick account" /></SelectTrigger>
                <SelectContent>
                  {accounts.filter((a) => !a.is_control).map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      <span className="font-mono text-[10px] mr-1">{a.code}</span>{a.name}{" "}
                      <span className="text-[10px] text-muted-foreground">({a.account_type})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Min amount (optional)</Label>
                <Input type="number" value={form.amount_min}
                        onChange={(e) => setForm({ ...form, amount_min: e.target.value })}
                        className="rounded-sm mt-1" />
              </div>
              <div>
                <Label>Max amount (optional)</Label>
                <Input type="number" value={form.amount_max}
                        onChange={(e) => setForm({ ...form, amount_max: e.target.value })}
                        className="rounded-sm mt-1" />
              </div>
            </div>
            <div>
              <Label>Limit to one bank (optional)</Label>
              <Select value={form.bank_account_id || "__any__"}
                      onValueChange={(v) => setForm({ ...form, bank_account_id: v === "__any__" ? "" : v })}>
                <SelectTrigger className="rounded-sm mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">All bank accounts</SelectItem>
                  {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} · {b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Voucher narration (optional)</Label>
              <Input value={form.narration || ""} onChange={(e) => setForm({ ...form, narration: e.target.value })}
                     placeholder="Leave blank to use the bank description"
                     className="rounded-sm mt-1" />
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Checkbox id="rule-enabled" checked={form.enabled}
                         onCheckedChange={(v) => setForm({ ...form, enabled: !!v })}
                         data-testid="rule-enabled" />
              <label htmlFor="rule-enabled">Enabled — run on every import + on demand</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenEdit(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy}
                    className="bg-accent hover:bg-accent/90 text-white"
                    data-testid="rule-save">
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Apply preview dialog ---- */}
      <Dialog open={!!applyPreview} onOpenChange={(o) => !o && setApplyPreview(null)}>
        <DialogContent className="max-w-3xl" data-testid="apply-preview">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-accent" />Run Bank Rules — preview
            </DialogTitle>
          </DialogHeader>
          {applyPreview && (
            <div className="space-y-3 text-sm">
              <div className="text-xs text-muted-foreground">
                <b>{applyPreview.matches.length}</b> unmatched bank entries match one of your enabled rules.
                Click "Post all" to create the vouchers atomically.
              </div>
              {applyPreview.matches.length === 0 ? (
                <div className="text-center text-muted-foreground py-6 border rounded-sm">
                  No matches. Try widening a pattern, disabling side filters, or checking amount ranges.
                </div>
              ) : (
                <div className="border rounded-sm max-h-[380px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Date</th>
                        <th className="p-2 text-left">Description</th>
                        <th className="p-2 text-left">Rule</th>
                        <th className="p-2 text-left">→ Account</th>
                        <th className="p-2 text-right">Amount</th>
                        <th className="p-2 text-left">Voucher</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applyPreview.matches.map((m) => (
                        <tr key={m.entry_id} className="border-t">
                          <td className="p-2">{m.date}</td>
                          <td className="p-2 max-w-[200px] truncate">{m.description}</td>
                          <td className="p-2 text-xs font-mono">{m.rule_name}</td>
                          <td className="p-2 text-xs"><span className="font-mono text-[10px]">{m.account_code}</span> {m.account_name}</td>
                          <td className="p-2 text-right tabular-nums font-mono">{fmtINR(m.amount)}</td>
                          <td className="p-2 text-[10px] font-mono uppercase">{m.preview_voucher_type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyPreview(null)}>Cancel</Button>
            {applyPreview && applyPreview.matches.length > 0 && (
              <Button onClick={applyCommit} disabled={busy}
                      className="bg-accent hover:bg-accent/90 text-white"
                      data-testid="apply-commit">
                {busy ? "Posting…" : `Post all ${applyPreview.matches.length} voucher(s)`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
